import { Alert, Breadcrumb, Button, Col, Collapse, Container, Modal, Row } from "react-bootstrap";
import AppSearch from "../../../components/common/AppSearch";
import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { Target } from "../../../models/Target";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Targets } from "../../../api/Targets";
import { useToasts } from "../../../contexts/toasts";
import { PagedResults } from "../../../models/generics";
import { useFullScreenLoader } from "../../../contexts/fullScreenLoader";
import { Flex, Pagination, Button as AntButton, Form, Input, Checkbox, Select } from "antd";
import { TargetItem } from "../../../components/list-items/TargetItem";
import ScriptEditor from "../../../components/common/ScriptEditor";
import { Link } from "react-router-dom";
import IconButton from "../../../components/common/IconButton";
import { accessLevelOptions, TARGET_EXAMPLE_PAYLOAD } from "../../../utils";
import AppLoader from "../../../components/common/AppLoader";

export default function TargetsList() {
  const [form] = Form.useForm();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const [targets, setTargets] = useState<Target[]>();
  const [target, setTarget] = useState<Target>();
  const [targetDefinition, setTargetDefinition] = useState<string>();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [totalTargets, setTotalTargets] = useState<number>();
  const [error, setError] = useState(false);
  const [definitionError, setDefinitionError] = useState("");
  const [showExample, setShowExample] = useState(false);

  const initTargets = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", `${currentPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (searchText.length) params.set("freeText", searchText);

    Targets.getTargets(params)
      .then(
        ({ data }) => setTargets((data as PagedResults<Target>).content),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('targets-error', message, `Targets Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      )
  }, [showToast, currentPage, searchText]);

  const reset = useCallback(() => {
    setTargets(undefined);
    setCurrentPage(1);
    setTotalTargets(undefined);
    initTargets();
  }, [initTargets]);

  useEffect(reset, [reset, currentPage, searchText]);

  const debounceSearch = useMemo(() => debounce(value => setSearchText(value), 500), []);

  const handleClose = useCallback(() => {
    setShowDeleteModal(false);
    setShowEditModal(false);
    setTarget(undefined);
    setTargetDefinition(undefined);
    setShowExample(false);
    form.resetFields();
    setDefinitionError("");
  }, [form]);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const handleEdit = useCallback((target: Target) => () => {
    setTarget(target);
    form.setFieldsValue({ name: target.name, description: target.description, isDefault: target.isDefault, accessLevel: target.accessLevel });
    setTargetDefinition(getTargetDefinition(target));
    setShowEditModal(true);
  }, [form]);

  const handleDelete = useCallback((target: Target) => () => {
    setTarget(target);
    setShowDeleteModal(true);
  }, []);

  const handleSubmit = useCallback((values: Partial<Target>) => {
    if (!targetDefinition) {
      setDefinitionError("Target Definition is required.");
      return
    }

    let definition;
    try {
      definition = JSON.parse(targetDefinition!);
    } catch (e) {
      setDefinitionError("Target Definition has the following error: " + e);
      return;
    }
    showLoader();
    const body = { name: values.name, description: values.description, isDefault: values.isDefault, ...definition, accessLevel: values.accessLevel };
    const promise = target ? Targets.editTarget(target.id!, body) : Targets.createTarget(body);

    promise.then(
      () => {
        hideLoader();
        showToast('target-success', `Target ${target ? 'updated' : 'created'} successfully`, 'Target Success', 'success');
        handleClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('target-error', message, `Target Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [handleClose, hideLoader, targetDefinition, reset, showLoader, showToast, target]);

  const deleteTarget = useCallback(() => {
    showLoader();

    Targets.deleteTarget(target!.id!)
      .then(
        () => {
          hideLoader();
          handleClose();
          reset();
          showToast('target-success', `Target removed successfully`, `Target Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('target-error', message, `Target Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, target, reset, showToast]);

  const toggleSmartEditExample = useCallback(() => setShowExample(v => !v), []);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(TARGET_EXAMPLE_PAYLOAD));
    showToast('text-copied', 'Value copied to clipboard!');
  }, [showToast]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!targets) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return targets!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No Targets found.</span>
        </Alert>
      )
      : (
        <Row className="gx-2">
          {targets!.map((item, index) => (
            <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-2">
              <TargetItem target={item} onEdit={handleEdit(item)} onDelete={handleDelete(item)} />
            </Col>
          ))}
        </Row>
      )
  }, [error, targets, handleDelete, handleEdit]);

  return (
    <Container fluid="md" className="mt-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Targets Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="align-items-center">
        <Col>
          <h4>Targets List</h4>
        </Col>

        <Col md={3}>
          <AppSearch placeholder="Search Target..." onChange={debounceSearch} />
        </Col>

        <Col className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create Target</span>
          </Button>
        </Col>
      </Row>

      <Row className="my-3">
        <Pagination
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          total={totalTargets}
          pageSize={20}
          current={currentPage}
          onChange={setCurrentPage}
          className="d-flex justify-content-center align-items-center"
        />
      </Row>

      {renderAsyncContent()}

      <Modal show={showEditModal} size="lg" onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{target ? 'Edit' : 'New'} Target</Modal.Title>
        </Modal.Header>
        <Form form={form} onFinish={handleSubmit} colon={false} layout="vertical" className="compact-form">
          <Modal.Body>
            <Alert className="p-2 mb-3 font-smaller" variant="primary">
              <Flex align="center">
                <FontAwesomeIcon icon={solid('info-circle')} />
                <Col className="px-3">
                  For affinities and tolerations, it is recommended to follow the{" "}
                  <a className="mx-auto me-md-0" href="https://kubernetes.io/docs/home/" target="_blank" rel="noreferrer">Official Documentation</a>
                </Col>
                <AntButton type="text" className="text-primary" onClick={toggleSmartEditExample} size="small">{showExample ? 'Hide' : 'Show'} Example</AntButton>
              </Flex>
              <Collapse in={showExample}>
                <div>
                  <Flex align="center" className="smart-update-example-container p-2 mt-2">
                    <pre className="m-0 font-monospace font-sm">{JSON.stringify(TARGET_EXAMPLE_PAYLOAD, null, 4)}</pre>
                    <IconButton tooltip="Copy to Clipboard" className="p-1" onClick={copyToClipboard}>
                      <FontAwesomeIcon icon={solid('clipboard')} size="lg" />
                    </IconButton>
                  </Flex>
                </div>
              </Collapse>
            </Alert>

            <Form.Item name="name" label="Name" className="mb-3" rules={[{ required: true, message: "Target Name is required" }]}>
              <Input placeholder="Target Name" />
            </Form.Item>

            <Form.Item
              name="accessLevel"
              label="Access Level"
              rules={[{ required: true, message: "Target Access Level is required" }]}
            >
              <Select placeholder="Select privacy level" allowClear className="w-100">
                {accessLevelOptions.map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <FontAwesomeIcon icon={option.icon} fixedWidth />
                    <span className="ms-2">{option.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} placeholder="Target Description" />
            </Form.Item>

            <Form.Item name="isDefault" extra="There should be just one default target" valuePropName="checked">
              <Checkbox>Default Target</Checkbox>
            </Form.Item>

            <ScriptEditor height={360} value={targetDefinition} onChange={setTargetDefinition} />
            {definitionError && (<p className="text-danger mb-0">{definitionError}</p>)}
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
            <Button type="submit" variant="success" size="sm">
              <FontAwesomeIcon icon={solid("save")} />
              <span className="ms-2">Save</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Target</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove Target <b>{target?.name}</b>?</p>

          <Alert variant="warning">
            <Flex align="center">
              <FontAwesomeIcon icon={solid('exclamation-triangle')} />
              <Col className="ms-3">
                <span>Remember, this operation cannot be undone.</span>
                <span className="d-block">All the Services associated to this Target will be affected.</span>
              </Col>
            </Flex>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteTarget}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

const getTargetDefinition = (target: Target) => {
  const newTarget: Partial<Target> = {
    // name: target.name,
    // description: target.description,
    affinity: target.affinity,
    env: target.env,
    tolerations: target.tolerations,
    // isDefault: target.isDefault,
    runtimeClassName: target.runtimeClassName,
  }
  return JSON.stringify(newTarget);
}